import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import * as mock from './mock.js';
import { messages, paletteItems } from './mock.js';
import { useStore } from './lib/store.js';
import { LibraryLink } from './library/LibraryLink.jsx';

// Fetch a real operator endpoint and normalize the response. Returns
// `null` on the initial render and on any failure so callers can render
// a proper loading/empty state instead of a hard-coded mock. The effect
// resets `data` to null on path/transform change and aborts any in-flight
// fetch on cleanup, so a stale response from a prior path can never land
// in the new state.
//
// `transform` is in the deps array, so callers MUST pass a stable
// reference (module-level function or memoized callback) — an inline
// arrow would re-trigger the effect on every render.
function useApi(path, transform = identity) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const ctrl = new AbortController();
    setData(null);
    fetch(path, { credentials: 'same-origin', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => setData(transform(j)))
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        // leave null — caller renders empty/error state
      });
    return () => ctrl.abort();
  }, [path, transform]);
  return data;
}

// Module-level transforms for useApi callers. Keep these stable so the
// hook's effect doesn't re-fire every render on a fresh transform identity.
const identity = (x) => x;

// /api/briefing/today returns the EntityWire envelope shape
// {urn, entity_type, schema_version, envelope, payload}; Placemat wants
// the flat payload, so unwrap here.
const unwrapBriefingPayload = (e) => e?.payload ?? null;


const ASSIGNEES = [
  { key: 'me',     label: 'me',     initials: 'SL' },
  { key: 'claire', label: 'claire', initials: 'CL' },
  { key: 'eve',    label: 'eve',    initials: 'EV' },
  { key: 'iris',   label: 'iris',   initials: 'IR' },
  { key: 'morgan', label: 'morgan', initials: 'MO' },
  { key: 'riley',  label: 'riley',  initials: 'RI' },
];

function Actor({ name, initials, agent, tag }) {
  return (
    <span className={`actor${agent ? ' agent' : ''}`}>
      <span className="av">{initials}</span>
      {name}
      {tag ? <span className="tag">{tag}</span> : null}
    </span>
  );
}

function ChatIconBtn({ to, title, size = 'sm', onOpen }) {
  return (
    <button
      className={`chatIcon${size === 'sm' ? ' sm' : ''}`}
      title={title || `Message ${to}`}
      aria-label={title || `Message ${to}`}
      onClick={(e) => { e.stopPropagation(); onOpen(to); }}
    />
  );
}

export function Overview() {
  const { agents, activity, onOpenChat: openChat } = useOutletContext();
  const today = 'Fri · Apr 17 · 09:14';

  // JOBE-0159.2: update browser tab title to match the renamed route.
  useEffect(() => { document.title = 'Command Center — Jobe'; }, []);

  const briefing = useApi('/api/briefing/today', unwrapBriefingPayload);
  const departments = useApi('/api/departments', normalizeDepartments) || [];

  // View-model slices (JOBE-0045.3): tasks/feed/day/reports come from
  // the Zustand store. Stream events (`tasks.changed`, `feed.appended`)
  // re-drive these slices reactively; absent a publisher they behave
  // exactly like the previous one-shot fetch.
  const loadTasks = useStore((s) => s.loadTasks);
  const loadFeed = useStore((s) => s.loadFeed);
  const loadDay = useStore((s) => s.loadDay);
  const loadReports = useStore((s) => s.loadReports);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  useEffect(() => { loadDay(); }, [loadDay]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const tasksSlice = useStore((s) => s.tasks);
  const feedSlice = useStore((s) => s.feed);
  const daySlice = useStore((s) => s.day);
  const reportsSlice = useStore((s) => s.reports);

  const taskList = tasksSlice.loaded && !tasksSlice.fallback ? tasksSlice.items : mock.tasks;
  const feedRows = feedSlice.loaded && !feedSlice.fallback ? feedSlice.rows : mock.feed;
  const day = daySlice.loaded && !daySlice.fallback
    ? { hours: daySlice.hours, items: daySlice.items }
    : { hours: mock.dayStrip, items: mock.myDay };
  const reportList = reportsSlice.loaded && !reportsSlice.fallback ? reportsSlice.list : mock.reports;

  return (
    <div className="desk">
      <div className="front">
        <Placemat today={today} briefing={briefing} />
        <DepartmentBlock departments={departments} />
        <TwoUp day={day} tasks={taskList} />
        <FeedCard feed={feedRows} />
      </div>
      <Sidecol onOpenChat={openChat} agents={agents} activity={activity} reports={reportList} />
    </div>
  );
}

function Placemat({ today, briefing }) {
  // briefing is null while /api/briefing/today is in-flight; an object
  // (possibly empty) once the BFF answers. With mocks gone we render
  // an honest empty-state instead of a hard-coded headline.
  if (!briefing) {
    return (
      <div className="placemat" data-today={today}>
        <div className="pmhead">
          <div>
            <div className="kicker" style={{ marginBottom: 4 }}>Good morning, Shawn</div>
            <h2 style={{ color: 'var(--ink-soft)' }}>No briefing yet.</h2>
          </div>
        </div>
      </div>
    );
  }
  const { headline, summary, agenda = [], departments = [], referencedDocuments = [] } = briefing;
  const headlineParts = (() => {
    if (!headline) return ['', ''];
    const idx = headline.indexOf(',');
    return idx === -1 ? [headline, ''] : [headline.slice(0, idx + 1), headline.slice(idx + 1).trim()];
  })();
  return (
    <div className="placemat" data-today={today}>
      <div className="pmhead">
        <div>
          <div className="kicker" style={{ marginBottom: 4 }}>Good morning, Shawn</div>
          <h2>{headlineParts[0]} {headlineParts[1] && <em>{headlineParts[1]}</em>}</h2>
          {summary && <p className="pmsummary">{summary}</p>}
        </div>
      </div>

      {departments.length > 0 && (
        <div className="pmdepartments">
          {departments.map((d, i) => (
            <section className="pmdept" key={`${d.name}-${i}`}>
              <h3>{d.name}</h3>
              {d.summary && <p className="pmdept-summary">{d.summary}</p>}
              {Array.isArray(d.tasks) && d.tasks.length > 0 && (
                <div className="pmdept-group">
                  <div className="pmdept-label">Tasks</div>
                  <ul className="pmlinks">
                    {d.tasks.map((t, j) => (
                      <li key={`${t.urn}-${j}`}>
                        <LibraryLink urn={t.urn} label={t.label} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(d.projects) && d.projects.length > 0 && (
                <div className="pmdept-group">
                  <div className="pmdept-label">Projects</div>
                  <ul className="pmlinks">
                    {d.projects.map((p, j) => (
                      <li key={`${p.urn}-${j}`}>
                        <LibraryLink urn={p.urn} label={p.label} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {agenda.length > 0 && (
        <div className="pmagenda">
          <h3>Agenda</h3>
          <ul className="pmlinks">
            {agenda.map((a, i) => (
              <li key={`${a.urn}-${i}`}>
                <LibraryLink urn={a.urn} label={a.label} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {referencedDocuments.length > 0 && (
        <div className="pmreferences">
          <h3>Source documents</h3>
          <ul className="pmlinks">
            {referencedDocuments.map((d, i) => (
              <li key={`${d.urn}-${i}`}>
                <LibraryLink urn={d.urn} label={d.label} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// JOBE-0067.9: status → tile-state CSS class.
//   active      → ok (green dot)
//   paused      → watch (amber dot)
//   unreachable → block (red dot)
//   default     → '' (neutral pencil)
const DEPT_STATUS_CLASS = { active: 'ok', paused: 'watch', unreachable: 'block' };

function DepartmentBlock({ departments }) {
  return (
    <div className="card departmentBlock">
      <h3>Department status <span className="more">Org overview →</span></h3>
      {departments.length === 0 ? (
        <div style={{ padding: '12px 0', color: 'var(--ink-soft)' }}>No departments yet.</div>
      ) : (
        <div className="tiles">
          {departments.map((d) => (
            <div
              key={d.slug}
              className={`tile ${DEPT_STATUS_CLASS[d.status] || ''}`}
              onClick={() => { window.location.hash = `#department/${d.slug}`; }}
              role="link"
            >
              <div className="hd"><div className="nm">{d.name}</div><div className="st" /></div>
              {d.status && d.status !== 'active' && (
                <div className="meta">status · {d.status}</div>
              )}
              {d.description && <div className="sig">{d.description}</div>}
              <div className="go">open →</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// normalizeDepartments adapts both the BFF response shape
// ({items: [...EntityWire], next_cursor}) and the in-repo mock array to
// the tile shape {slug, name, description, status, lead}.
function normalizeDepartments(raw) {
  const items = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.items) ? raw.items : []);
  return items.map((it) => {
    if (it && it.urn && it.payload) {
      const slug = slugFromURN(it.urn);
      const p = it.payload || {};
      return {
        slug,
        name: p.displayName || slug,
        description: p.description || '',
        status: p.status || 'active',
        lead: p.lead || '',
      };
    }
    return it;
  });
}

function slugFromURN(u) {
  if (!u) return '';
  const i = u.lastIndexOf(':');
  return i === -1 ? u : u.slice(i + 1);
}

function TwoUp({ day, tasks }) {
  const hours = day?.hours || [];
  const items = day?.items || [];
  return (
    <div className="twoUp">
      <div className="card">
        <h3>My Day <span className="more">Timeline →</span></h3>
        <div className="dayStrip">
          {hours.map((h, i) => {
            const cls = [
              'hr',
              h.blk ? 'blk-short' : '',
              h.now ? 'blk-now now' : '',
              h.soft ? 'blk-soft' : '',
            ].filter(Boolean).join(' ');
            return <div key={i} className={cls}>{h.h}{h.blk && <div className="blk" />}</div>;
          })}
        </div>
        <div className="mydayA">
          {items.map((it, i) => (
            <div key={i} className={`item${it.now ? ' now' : ''}`}>
              <div className="t">{it.time}</div>
              <div className="bar" />
              <div>
                <div className="ttl">{it.title}</div>
                <div className="sub">{it.sub}</div>
              </div>
              <div className="who">{it.who}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Tasks <span className="more">All tasks →</span></h3>
        <div className="tasks">
          {tasks.map((t, i) => (
            <div key={i} className={`t${t.done ? ' done' : ''}`}>
              <div className="box" />
              <div className="ttl">{t.title}</div>
              <div className="meta">{t.meta}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeedCard({ feed }) {
  return (
    <div className="card">
      <h3>Feed · last 6 hours <span className="more">Full feed →</span></h3>
      <div className="feedDense">
        {feed.map((r, i) => (
          <div key={i} className={`row ${r.state}`}>
            <span className="t">{r.time}</span>
            <span className="d" />
            <span className={`k ${r.k}`}>{r.k.toUpperCase()}</span>
            <div className="tt">
              <span className="src">{r.src}</span>
              <span>{r.tt}</span>
            </div>
            <span className="tag">{r.tag}</span>
          </div>
        ))}
        <div className="foot">
          <span>showing {feed.length} / 2,148 in 24h</span>
          <span>filter: all · last 6h</span>
        </div>
      </div>
    </div>
  );
}

function Sidecol({ onOpenChat, agents, activity, reports }) {
  return (
    <div className="sidecol">
      <WaitingOnYou onOpenChat={onOpenChat} />
      <AskBox />
      <Roster onOpenChat={onOpenChat} agents={agents} activity={activity} />
      <ReportsShelf reports={reports} />
    </div>
  );
}

function WaitingOnYou({ onOpenChat }) {
  return (
    <div className="card">
      <h3>Waiting on you <span className="more">Message center →</span></h3>
      <div className="msgs">
        {messages.map((m, i) => (
          <div key={i} className="m" onClick={() => onOpenChat(m.lede.split(' · ')[0])}>
            <span className={`badge ${m.cls}`}>{m.label}</span>
            <div className="tt"><b>{m.title}</b><span className="lede">{m.lede}</span></div>
            <span className="age">→</span>
          </div>
        ))}
      </div>
      <QuickAddTask />
    </div>
  );
}

function QuickAddTask() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('me');
  const [done, setDone] = useState(false);

  const submit = () => {
    if (!title.trim()) return;
    // Stub: POST /api/tasks does not exist yet (see JOBE-0010.8 backlog).
    // Log for now; keep optimistic UI.
    console.info('[task:add]', { title, assignee, note });
    setDone(true);
    setTitle(''); setNote(''); setAssignee('me'); setOpen(false);
    setTimeout(() => setDone(false), 1800);
  };

  const cls = ['qadd', open ? 'open' : '', done ? 'done' : ''].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="qtitle">
        <span>Quick add task</span>
        <span className="expand" onClick={() => setOpen((v) => !v)}>
          {open ? '– hide' : '+ assignee / note'}
        </span>
      </div>
      <input
        className="ttl"
        placeholder="What needs doing?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
      />
      <div className="extras">
        <div className="assignRow">
          <span className="lbl">Assign</span>
          {ASSIGNEES.map((a) => (
            <span
              key={a.key}
              className={`a${assignee === a.key ? ' on' : ''}`}
              onClick={() => setAssignee(a.key)}
            >
              <span className="av">{a.initials}</span>{a.label}
            </span>
          ))}
        </div>
        <textarea
          placeholder="Comment (optional)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="qfoot">
        <span className="hint">
          <span className="toast">✓ task added</span>
          <span className="prompt">↵ add</span>
        </span>
        <button className="add" disabled={!title.trim()} onClick={submit}>Add task</button>
      </div>
    </div>
  );
}

function AskBox() {
  return (
    <div className="askBox">
      <h4>Eve wants to know</h4>
      <div className="q">Should I elevate the Platform audit backlog to a resource request, or keep watching?</div>
      <div className="actions">
        <button className="primary">Elevate</button>
        <button>Watch</button>
        <button>Later</button>
      </div>
    </div>
  );
}

const PHASE_STATE = { Running: 'ok', Pending: 'watch', Failed: 'block', Error: 'block' };

function Roster({ onOpenChat, agents, activity }) {
  const act = activity || {};
  const [scope, setScope] = useState('direct');
  const [q, setQ] = useState('');

  // /api/agents drives the list. Each row reflects whatever the BFF
  // returned; if the API is empty the panel renders an honest empty
  // state instead of a hard-coded seed. Direct-reports / department-heads
  // chips currently both default to "all" scope until the API ships
  // structured roster metadata.
  const rows = useMemo(() => {
    const apiList = agents || [];
    return apiList.map((a) => ({
      name: a.name,
      initials: (a.name || '??').slice(0, 2).toUpperCase(),
      state: PHASE_STATE[a.phase] || '',
      role: `${a.phase || 'Unknown'} · AGT`,
      reports: undefined,
      agent: true,
      scope: ['direct', 'department-heads', 'all'],
      keys: `${a.name || ''} ${`${a.phase || 'Unknown'} · AGT`}`.toLowerCase(),
      real: true,
    }));
  }, [agents]);

  const filtered = rows
    .filter((r) => {
      if (!r.scope.includes(scope)) return false;
      if (!q.trim()) return true;
      return r.keys.toLowerCase().includes(q.trim().toLowerCase())
          || r.name.toLowerCase().includes(q.trim().toLowerCase());
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  return (
    <div className="card">
      <h3>Roster <span className="more">All actors →</span></h3>
      <div className="rosterFilter">
        <div className="chips">
          {[
            { k: 'direct', l: 'Direct reports' },
            { k: 'department-heads', l: 'Department heads' },
            { k: 'all', l: 'All actors' },
          ].map(({ k, l }) => (
            <span key={k} className={`chip${scope === k ? ' on' : ''}`} onClick={() => setScope(k)}>{l}</span>
          ))}
        </div>
        <div className="search">
          <span className="glyph">⌕</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, role…" />
        </div>
        <div className="count">{filtered.length} of {rows.length}</div>
      </div>
      <div className="roster">
        {rows.length === 0 ? (
          <div style={{ padding: '12px 0', color: 'var(--ink-soft)' }}>No agents yet.</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '12px 0', color: 'var(--ink-soft)' }}>No matches.</div>
        ) : (
          filtered.map((r) => (
            <div
              key={r.name}
              className={`r ${r.state} r-clickable`}
              title="backed by /api/agents"
              onClick={() => onOpenChat(r.name.toLowerCase())}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenChat(r.name.toLowerCase()); } }}
            >
              <span className="st" />
              <Actor name={r.name} initials={r.initials} agent={r.agent} />
              <span className="role">
                {r.role}
                {r.reports && <span className="reports"> (reports: {r.reports})</span>}
              </span>
              {r.agent && <ActivityPill ev={act[r.name.toLowerCase()]} />}
              <ChatIconBtn to={r.name.toLowerCase()} onOpen={onOpenChat} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ActivityPill renders the JOBE-0043 phase next to an agent. No event
// yet → no pill (don't paint stale "idle" before the first signal).
function ActivityPill({ ev }) {
  if (!ev || !ev.phase) return null;
  const { phase, tool } = ev;
  const label = phase === 'tool' && tool ? `tool: ${tool}` : phase;
  return <span className={`activityPill ${phase}`} title={label}>{label}</span>;
}

function ReportsShelf({ reports }) {
  return (
    <div className="card">
      <h3>Reports shelf <span className="more">All reports →</span></h3>
      <div className="shelf">
        {reports.map((r, i) => (
          <div key={i} className={`r${r.today ? ' today' : ''}`}>
            <span>{r.today ? <>{r.name.split(' · ')[0]} · <b>today</b></> : r.name}</span>
            <span>{r.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
/* ---------- Command palette ---------- */
export function CommandPalette({ onClose, onOpenChat }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return paletteItems;
    return paletteItems.filter((it) =>
      it.name.toLowerCase().includes(term) || (it.sub || '').toLowerCase().includes(term),
    );
  }, [q]);

  const groups = useMemo(() => {
    const out = {};
    filtered.forEach((it) => { (out[it.group] ||= []).push(it); });
    return Object.entries(out);
  }, [filtered]);

  useEffect(() => { setCursor(0); }, [q]);

  const activate = (it) => {
    if (!it) return;
    if (it.group === 'Jump' && it.sub === 'actor') onOpenChat(it.name.toLowerCase());
    // Other groups are stubs for now — see JOBE-0010.8 backlog.
    onClose();
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); activate(filtered[cursor]); }
  };

  let flat = -1;
  return (
    <div className="scrim" onClick={onClose}>
      <div className="palette" onClick={(e) => e.stopPropagation()}>
        <div className="pinput">
          <span className="glyph">⌖</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask, jump, invoke…"
          />
          <kbd>esc</kbd>
        </div>
        <div className="plist">
          {groups.map(([g, items]) => (
            <div className="pgroup" key={g}>
              <div className="glbl">{g}</div>
              {items.map((it) => {
                flat += 1;
                const active = flat === cursor;
                return (
                  <div
                    key={`${g}:${it.name}`}
                    className={`pitem ${it.cls}${active ? ' on' : ''}`}
                    onMouseEnter={(() => { const at = flat; return () => setCursor(at); })()}
                    onClick={() => activate(it)}
                  >
                    <span className="ic">{it.icon}</span>
                    <span className="nm">{it.name}{it.sub && <span className="sub">{it.sub}</span>}</span>
                    <span className="kb">{it.kb || ''}</span>
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="pgroup"><div className="glbl">No matches</div></div>
          )}
        </div>
        <div className="pfoot">
          <span>↑↓ navigate · ↵ select · esc close</span>
          <span>palette · v0</span>
        </div>
      </div>
    </div>
  );
}
